import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} text-center mb-8 md:mb-12`}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-sparkling-line text-[#1B3D2A]" />
          </div>
        </div>
        <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">{label}</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">{title}</h2>
      {subtitle && <p className="mt-3 text-sm text-[#5A6B5A]">{subtitle}</p>}
      <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  const delayClass = delay === 0 ? '' : delay === 1 ? 'scroll-reveal-delay-1' : delay === 2 ? 'scroll-reveal-delay-2' : delay === 3 ? 'scroll-reveal-delay-3' : delay === 4 ? 'scroll-reveal-delay-4' : delay === 5 ? 'scroll-reveal-delay-5' : 'scroll-reveal-delay-6';
  return (
    <div ref={ref} className={`scroll-reveal ${delayClass} ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function About() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const milestones = [
    { year: '2026', title: '持續深耕', desc: '服務超過 10,000 位聽損者，成為台北地區信賴的聽力保健品牌' },
    { year: '2022', title: '線上預約', desc: '推出線上預約系統，讓民眾更方便安排聽力檢測' },
    { year: '2019', title: '專業認證', desc: '全體聽力師通過國家認證，提供更高品質的專業服務' },
    { year: '2017', title: '擴展服務', desc: '開設第二家門市，並引進國際先進聽力檢測設備' },
    { year: '2015', title: '品牌創立', desc: '富聆聽健康在台北成立，專注聽力保健與助聽器選配服務' },
  ];

  const team = [
    {
      name: '林育誠',
      title: '首席聽力師',
      desc: '國立陽明大學聽力學碩士，擁有 15 年臨床經驗，專精於兒童聽力評估與助聽器選配。',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20audiologist%20in%20white%20coat%20warm%20smile%20portrait%20soft%20lighting%20modern%20clinic%20background%20clean%20professional&width=300&height=300&seq=50&orientation=squarish',
    },
    {
      name: '王美琪',
      title: '資深聽力師',
      desc: '臺北醫學大學聽語系畢業，擅長老年聽力保健與助聽器調適，服務親切有耐心。',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20audiologist%20in%20white%20coat%20warm%20smile%20portrait%20soft%20lighting%20modern%20clinic%20background%20clean%20professional&width=300&height=300&seq=51&orientation=squarish',
    },
    {
      name: '張志宏',
      title: '助聽器選配師',
      desc: '專精於各品牌助聽器技術規格，能為不同聽損程度找到最適合的機型。',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20male%20hearing%20aid%20specialist%20in%20white%20coat%20warm%20smile%20portrait%20soft%20lighting%20modern%20clinic%20background%20clean%20professional&width=300&height=300&seq=52&orientation=squarish',
    },
    {
      name: '李雅婷',
      title: '聽力保健顧問',
      desc: '專注於聽力衛教與社群推廣，致力於讓更多人了解聽力保健的重要性。',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20health%20consultant%20in%20white%20coat%20warm%20smile%20portrait%20soft%20lighting%20modern%20clinic%20background%20clean%20professional&width=300&height=300&seq=53&orientation=squarish',
    },
  ];

  const steps = [
    { icon: 'ri-calendar-line', title: '線上預約', desc: '透過網站或電話預約免費聽力檢測，選擇方便的時段。' },
    { icon: 'ri-user-search-line', title: '專業檢測', desc: '由國家認證聽力師進行完整聽力評估，了解聽損類型與程度。' },
    { icon: 'ri-chat-3-line', title: '需求諮詢', desc: '根據檢測結果與生活需求，提供個人化的助聽器選配建議。' },
    { icon: 'ri-settings-4-line', title: '精準調校', desc: '試戴助聽器並進行精細調校，確保最佳聆聽效果與舒適度。' },
    { icon: 'ri-shield-check-line', title: '售後追蹤', desc: '定期回訪調整，提供終生保養與諮詢服務，守護您的聽力健康。' },
  ];

  const stats = [
    { num: '10,000+', label: '服務聽損者', icon: 'ri-user-smile-line' },
    { num: '15', label: '年專業經驗', icon: 'ri-award-line' },
    { num: '4', label: '專業聽力師', icon: 'ri-group-line' },
    { num: '98%', label: '顧客滿意度', icon: 'ri-heart-3-line' },
  ];

  const philosophies = [
    {
      icon: 'ri-heart-pulse-line',
      title: '以人為本',
      desc: '我們相信每位聽損者都值得被理解與尊重。不僅是提供助聽器，更是陪伴您找回與世界溝通的橋樑。',
    },
    {
      icon: 'ri-award-line',
      title: '專業至上',
      desc: '全體聽力師皆具國家認證資格，持續進修最新聽力學知識與技術，確保提供最專業的服務品質。',
    },
    {
      icon: 'ri-hand-heart-line',
      title: '溫暖陪伴',
      desc: '聽力問題往往讓人感到孤單，我們以耐心和同理心傾聽每位顧客的需求，讓您感受到被重視與關懷。',
    },
  ];

  const storyHighlights = [
    { icon: 'ri-hospital-line', title: '專業門市', desc: '全台 8 家直營門市' },
    { icon: 'ri-customer-service-2-line', title: '一對一服務', desc: '專屬聽力師全程陪伴' },
    { icon: 'ri-shield-check-line', title: '品質保證', desc: '國家認證專業團隊' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* ===== Hero ===== */}
        <section className="relative text-white pt-8 md:pt-14 pb-8 md:pb-14">
          {/* Decorative Layer (clipped) */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Solid Dark Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#0F2A1C]" />
            {/* Background Image - subtle texture only */}
            <div className="absolute inset-0 opacity-[0.06]">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20modern%20hearing%20clinic%20interior%20with%20professional%20team%20members%20consulting%20elderly%20patient%20in%20bright%20spacious%20room%20with%20natural%20light%20green%20botanical%20elements%20caring%20professional%20atmosphere%20high%20quality%20editorial%20photography%20soft%20tones&width=1600&height=700&seq=about-hero-2026&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Subtle Geometric Pattern */}
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_30%,_#D4A574_1px,_transparent_1px),radial-gradient(circle_at_80%_70%,_#D4A574_1px,_transparent_1px)] bg-[length:60px_60px,80px_80px]" />
            {/* Decorative Glow */}
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#D4A574]/8 blur-[120px]" />
            <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#2E8B57]/12 blur-[100px]" />
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 md:gap-8">
              {/* Left: Title & Description */}
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-px h-5 bg-[#D4A574]" />
                  <span className="text-xs font-semibold tracking-[0.2em] text-[#D4A574] uppercase">
                    富聆聽健康 · 自 2015
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
                  專業守護，
                  <span className="block text-[#D4A574] mt-2 md:mt-3">聽見美好生活</span>
                </h1>
                <p className="mt-3 md:mt-4 text-white/88 text-base md:text-lg leading-relaxed max-w-xl">
                  富聆聽健康以「讓每個人都能聽見世界的美好」為使命。
                  我們提供專業的聽力檢測、助聽器選配與聽力保健諮詢，
                  用溫暖與專業陪伴每一位聽損者，找回與世界的連結。
                </p>
              </div>

              {/* Right: Key Stats */}
              <div className="flex items-center gap-3 md:gap-4 shrink-0">
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">10,000+</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">服務聽損者</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">15</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">年專業經驗</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">8</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">直營門市</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">98%</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">顧客滿意度</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none">
            <path d="M0 100L60 90C120 80 240 60 360 50C480 40 600 38 720 44C840 50 960 62 1080 68C1200 74 1320 74 1380 74L1440 74V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="white"/>
          </svg>
        </section>

        {/* ===== Stats Bar ===== */}
        <div className="relative z-10 -mt-10 md:-mt-14 px-6 md:px-10 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="grid grid-cols-2 md:grid-cols-4">
                {stats.map((stat, idx) => (
                  <div
                    key={stat.label}
                    className={`flex items-center gap-3 md:gap-4 p-5 md:p-7 ${idx !== 0 ? 'border-t md:border-t-0 md:border-l border-[#E8E0D6]' : ''}`}
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                        <i className={`${stat.icon} text-lg md:text-xl text-[#1B3D2A]`} />
                      </div>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-[#1B3D2A]">{stat.num}</div>
                      <div className="text-sm text-[#5A6B5A]">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== 品牌故事 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Our Story" title="品牌故事" subtitle="從一間小門市開始，到服務上萬位聽損者的專業品牌" />

          <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-12">
            <SectionReveal className="lg:w-1/2">
              <div className="rounded-2xl overflow-hidden h-64 md:h-80 lg:h-[460px] border border-[#E8E0D6] relative group">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20modern%20hearing%20clinic%20interior%20with%20professional%20equipment%20and%20soft%20natural%20lighting%20caring%20atmosphere%20green%20and%20cream%20tones%20high%20quality%20photography%20wide%20angle&width=700&height=500&seq=60&orientation=landscape"
                  alt="富聆聽門市"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#1B3D2A]/0 group-hover:bg-[#1B3D2A]/10 transition-colors duration-500" />
              </div>
            </SectionReveal>

            <div className="lg:w-1/2 flex flex-col justify-center">
              <SectionReveal delay={1}>
                <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A] mb-4">
                  讓每個人都能聽見世界的美好
                </h3>
                <p className="text-sm md:text-base text-[#5A6B5A] leading-relaxed mb-4">
                  富聆聽健康創立於 2015 年，由一群對聽力保健充滿熱忱的專業人士組成。
                  我們深知聽力損失不只是生理上的問題，更可能影響到人際關係、心理健康與生活品質。
                </p>
                <p className="text-sm md:text-base text-[#5A6B5A] leading-relaxed mb-6">
                  因此，我們不只是「賣助聽器」，而是提供從聽力檢測、評估、選配到售後調校的完整服務。
                  每一個環節，都由國家認證聽力師親自把關，確保您獲得最適合的聽力解決方案。
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {storyHighlights.map((item) => (
                    <div key={item.title} className="bg-[#FAF8F5] rounded-xl border border-[#E8E0D6]/60 p-4 text-center">
                      <div className="w-8 h-8 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center mx-auto mb-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${item.icon} text-[#1B3D2A] text-sm`} />
                        </div>
                      </div>
                      <div className="text-sm font-bold text-[#1B3D2A]">{item.title}</div>
                      <div className="text-xs text-[#5A6B5A] mt-0.5">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>

        {/* ===== 品牌理念 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Our Philosophy" title="品牌理念" subtitle="三大核心信念，指引我們每一次服務" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {philosophies.map((item, idx) => (
              <SectionReveal key={item.title} delay={idx}>
                <div className="group bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-10 text-center hover:border-[#D4A574]/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[#FAF8F5] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center mx-auto group-hover:bg-[#1B3D2A]/20 transition-colors duration-300">
                      <div className="w-7 h-7 flex items-center justify-center">
                        <i className={`${item.icon} text-2xl text-[#1B3D2A]`} />
                      </div>
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-[#1B3D2A]">{item.title}</h3>
                    <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>

        {/* Wave: white to warm */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== 專業團隊 ===== */}
        <div className="bg-[#FAF8F5] py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader label="Professional Team" title="專業團隊" subtitle="由國家認證聽力師組成的專業團隊，為您的聽力健康把關" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {team.map((member, idx) => (
                <SectionReveal key={member.name} delay={idx}>
                  <div className="group bg-white rounded-2xl overflow-hidden border border-[#E8E0D6] hover:border-[#D4A574]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="w-full h-52 md:h-64 overflow-hidden relative">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-[#1B3D2A]/0 group-hover:bg-[#1B3D2A]/20 transition-colors duration-300" />
                    </div>
                    <div className="p-5 md:p-6">
                      <h3 className="text-lg font-bold text-[#1B3D2A]">{member.name}</h3>
                      <p className="text-sm text-[#D4A574] font-medium mt-1">{member.title}</p>
                      <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">{member.desc}</p>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave: warm to white */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== 服務流程 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Service Process" title="服務流程" subtitle="簡單五步驟，開啟您的聽力健康旅程" />

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* 左側步驟列表 */}
            <div className="lg:w-1/2 space-y-3">
              {/* Progress bar */}
              <div className="hidden md:flex items-center gap-3 mb-6">
                <div className="flex-1 h-1.5 bg-[#E8E0D6] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1B3D2A] rounded-full transition-all duration-500"
                    style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-[#1B3D2A]">
                  {activeStep + 1} / {steps.length}
                </span>
              </div>

              {steps.map((step, idx) => (
                <button
                  type="button"
                  key={step.title}
                  onClick={() => setActiveStep(idx)}
                  className={`w-full flex items-center gap-4 p-5 rounded-xl transition-all duration-300 text-left border min-h-[44px] ${
                    activeStep === idx
                      ? 'bg-[#1B3D2A] text-white border-[#1B3D2A] shadow-md'
                      : 'bg-white border-[#E8E0D6] hover:border-[#D4A574]/50'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${activeStep === idx ? 'bg-white/20' : 'bg-[#1B3D2A]/10'}`}>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${step.icon} text-xl ${activeStep === idx ? 'text-white' : 'text-[#1B3D2A]'}`} />
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-bold">
                      <span className={`opacity-60 mr-1.5 ${activeStep === idx ? 'text-white/60' : 'text-[#9A8A7A]'}`}>步驟 {idx + 1}</span>
                      {step.title}
                    </div>
                    <p className={`text-sm mt-0.5 leading-relaxed ${activeStep === idx ? 'text-white/80' : 'text-[#5A6B5A]'}`}>
                      {step.desc}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* 右側圖片 */}
            <SectionReveal className="lg:w-1/2" delay={1}>
              <div className="relative rounded-2xl overflow-hidden h-64 md:h-80 lg:h-full border border-[#E8E0D6] group">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20audiologist%20consulting%20with%20elderly%20patient%20in%20modern%20warm%20clinic%20room%20explaining%20hearing%20test%20results%20caring%20atmosphere%20green%20tones%20high%20quality%20photography&width=600&height=500&seq=61&orientation=landscape"
                  alt="聽力檢測諮詢"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-[#1B3D2A]/80 backdrop-blur-sm p-4 md:p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${steps[activeStep].icon} text-lg text-white`} />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">
                        步驟 {activeStep + 1}：{steps[activeStep].title}
                      </div>
                      <p className="text-sm text-white/80 mt-0.5">{steps[activeStep].desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>

        {/* Wave: white to warm */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== 品牌里程碑 ===== */}
        <div className="bg-[#FAF8F5] py-10 md:py-14 relative">
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
            <div className="text-[20vw] font-extrabold text-[#1B3D2A]/[0.03] whitespace-nowrap">
              HISTORY
            </div>
          </div>

          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto relative">
            <SectionHeader label="Milestones" title="品牌里程碑" subtitle="從創立到深耕，每一步都踏實向前" />

            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-[#E8E0D6] -translate-x-1/2" />

              <div className="space-y-2 md:space-y-3">
                {milestones.map((m, idx) => (
                  <SectionReveal key={m.year} delay={idx % 2}>
                    <div className={`flex flex-col md:flex-row items-center gap-3 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`md:w-1/2 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="group inline-block text-left w-full md:w-auto">
                          <div className="relative bg-white rounded-xl border border-[#E8E0D6] p-4 md:p-6 hover:border-[#1B3D2A]/25 hover:shadow-sm transition-all duration-300 overflow-hidden">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#D4A574] rounded-l-xl transition-all duration-300 group-hover:w-1.5" />
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#1B3D2A] text-white px-2.5 py-1 rounded-full">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-calendar-line text-xs" />
                                </div>
                                {m.year}
                              </span>
                            </div>
                            <h3 className="text-sm font-bold text-[#1B3D2A]">{m.title}</h3>
                            <p className="mt-1 text-xs md:text-sm text-[#5A6B5A] leading-relaxed">{m.desc}</p>
                          </div>
                        </div>
                      </div>

                      {/* Timeline dot */}
                      <div className="relative shrink-0 z-10 hidden md:flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full bg-[#1B3D2A] border-[3px] border-[#FAF8F5] shadow-sm flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        </div>
                        <div className="absolute w-10 h-10 rounded-full border border-[#1B3D2A]/10" />
                      </div>

                      {/* Mobile dot */}
                      <div className="md:hidden relative shrink-0 z-10 flex items-center justify-center">
                        <div className="w-4 h-4 rounded-full bg-[#1B3D2A] border-[2px] border-[#FAF8F5] shadow-sm" />
                      </div>

                      <div className="md:w-1/2" />
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Wave: warm to white */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== 門市據點 — 亞洲佈局 ===== */}
        <div className="relative overflow-hidden">
          {/* Asia Vision Hero Banner */}
          <div className="relative bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#143020] py-12 md:py-20 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20stylized%20minimalist%20asia%20map%20illustration%20with%20glowing%20connection%20lines%20radiating%20from%20Taiwan%20island%20to%20major%20asian%20cities%20dots%20connected%20warm%20golden%20amber%20lines%20dark%20emerald%20green%20background%20elegant%20corporate%20style%20consistent%20with%20hearing%20healthcare%20brand%20identity&width=1600&height=700&seq=asia-map-1&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top opacity-25"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#0F2A1C]/80 via-transparent to-[#143020]/90" />

            <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center">
              <SectionReveal>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-global-line text-[#D4A574]" />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">Asia Vision</span>
                </div>
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">
                  立足台灣<span className="text-[#D4A574]"> · </span>佈局亞洲
                </h2>
                <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                  以台灣八家直營門市為基石，富聆聽健康正積極拓展亞洲版圖，
                  將專業聽力照護服務帶到更多需要的人身邊。
                </p>
              </SectionReveal>

              {/* Asia expansion nodes */}
              <SectionReveal delay={1} className="mt-8 md:mt-10">
                <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                  {/* Taiwan Hub */}
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#D4A574] flex items-center justify-center">
                      <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center">
                        <i className="ri-map-pin-fill text-white text-xl md:text-2xl" />
                      </div>
                    </div>
                    <div className="mt-2 px-3 py-1 bg-[#D4A574]/20 rounded-full">
                      <span className="text-xs md:text-sm font-bold text-[#D4A574]">台北 · 總部</span>
                    </div>
                    <span className="text-[10px] text-white/50 mt-1">8 家營運中</span>
                  </div>

                  {/* Connection dots */}
                  {[
                    { city: '東京', flag: 'ri-flag-line', year: '2027' },
                    { city: '首爾', flag: 'ri-flag-line', year: '2027' },
                    { city: '上海', flag: 'ri-flag-line', year: '2028' },
                    { city: '新加坡', flag: 'ri-flag-line', year: '2028' },
                    { city: '曼谷', flag: 'ri-flag-line', year: '2029' },
                    { city: '吉隆坡', flag: 'ri-flag-line', year: '2029' },
                  ].map((node, idx) => (
                    <div key={node.city} className="flex flex-col items-center group">
                      <div className="relative">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-dashed border-[#D4A574]/40 flex items-center justify-center group-hover:border-[#D4A574] group-hover:border-solid transition-all duration-300">
                          <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                            <i className="ri-building-line text-[#D4A574]/60 group-hover:text-[#D4A574] text-sm md:text-base transition-colors" />
                          </div>
                        </div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#2E8B57] flex items-center justify-center">
                          <div className="w-2 h-2 flex items-center justify-center">
                            <i className="ri-add-line text-white text-[8px]" />
                          </div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="text-xs font-semibold text-white/80 group-hover:text-white transition-colors">{node.city}</span>
                      </div>
                      <span className="text-[10px] text-[#D4A574]/60">{node.year}</span>
                    </div>
                  ))}
                </div>
              </SectionReveal>
            </div>

            {/* Bottom wave to white */}
            <svg className="absolute bottom-0 left-0 w-full h-auto block" viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none">
              <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="white" />
            </svg>
          </div>

          {/* 台灣營運據點 */}
          <div className="px-6 md:px-10 lg:px-16 pt-8 md:pt-12 pb-6 md:pb-8 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-6 md:mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-map-2-line text-[#1B3D2A] text-sm" />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">Taiwan Operations</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">台灣營運據點</h3>
                <p className="text-sm text-[#5A6B5A] mt-1">全台八家直營門市，專業聽力師為您服務</p>
              </div>
              <a
                href="https://www.google.com/maps?q=台北市大安區忠孝東路四段15號"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3D2A] hover:text-[#D4A574] transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line" />
                </div>
                在 Google 地圖檢視全部門市
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { name: '台北門市', addr: '台北市大安區忠孝東路四段15號5樓之1', phone: '(02)2781-0069', hours: ['週一至週五 09:00–12:00、13:30–18:00', '週六 09:00–12:00、13:30–17:00', '週日休息'], tag: '總店', isHQ: true },
                { name: '板橋門市', addr: '新北市板橋區中正路93號1樓', phone: '(02)2966-5319', hours: ['週一至週五 09:00–18:00', '週六 09:00–17:00', '週日休息'], tag: '分店', isHQ: false },
                { name: '桃園門市', addr: '桃園市桃園區復興路407號1樓', phone: '(03)331-5159', hours: ['週一至週三及週五 09:00–18:00', '週四 13:30–18:00', '週六 09:00–17:00', '週日休息'], tag: '分店', isHQ: false },
                { name: '新竹門市', addr: '新竹市東區中華路二段675號1樓', phone: '(03)524-9879', hours: ['週一至週五 09:00–18:00', '週六 09:00–17:00', '週日休息'], tag: '分店', isHQ: false },
                { name: '台中門市', addr: '台中市北區三民路三段46-1號1樓', phone: '(04)2228-7369', hours: ['週一至週五 09:00–18:00', '週六 09:00–17:00', '週日休息'], tag: '分店', isHQ: false },
                { name: '豐原門市', addr: '台中市豐原區安康路93號', phone: '(04)2515-0179', hours: ['週二至週六 08:00–17:00', '週一及週日休息'], tag: '分店', isHQ: false },
                { name: '嘉義門市', addr: '嘉義市西區民生北路283號1樓', phone: '(05)223-1649', hours: ['週一至週五 09:00–18:00', '週六 09:00–17:00', '週日休息'], tag: '分店', isHQ: false },
                { name: '高雄門市', addr: '高雄市三民區自由一路68號', phone: '(07)251-1209', hours: ['週二至週六 09:00–18:00', '週一及週日休息'], tag: '分店', isHQ: false },
              ].map((store, idx) => (
                <SectionReveal key={store.name} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-[#E8E0D6] hover:border-[#1B3D2A]/30 hover:-translate-y-1 transition-all duration-300 overflow-hidden h-full flex flex-col">
                    {/* Card Header */}
                    <div className={`flex items-center gap-2.5 px-4 py-3 ${store.isHQ ? 'bg-[#1B3D2A]' : 'bg-[#2D5A3D]'}`}>
                      <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${store.isHQ ? 'ri-store-2-fill' : 'ri-store-3-fill'} text-white text-sm`} />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-white tracking-wide">{store.name}</span>
                      <span className={`ml-auto text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full ${store.isHQ ? 'bg-[#D4A574] text-[#1B3D2A]' : 'bg-white/15 text-[#D4A574]'}`}>{store.tag}</span>
                    </div>

                    {/* Card Body */}
                    <div className="p-4 md:p-5 flex-1 flex flex-col">
                      {/* Address Row */}
                      <div className="flex items-center gap-3 pb-3 border-b border-[#E8E0D6]/60">
                        <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] flex items-center justify-center shrink-0">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-map-pin-line text-[#1B3D2A] text-sm" />
                          </div>
                        </div>
                        <p className="text-xs text-[#1B3D2A] font-medium leading-relaxed">{store.addr}</p>
                      </div>

                      {/* Phone Row */}
                      <div className="flex items-start gap-3 py-3 border-b border-[#E8E0D6]/60">
                        <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] flex items-center justify-center shrink-0">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-phone-line text-[#1B3D2A] text-sm" />
                          </div>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold text-[#9A8A7A] mb-0.5">電話</p>
                          <p className="text-xs text-[#1B3D2A] font-semibold tracking-wide">{store.phone}</p>
                        </div>
                      </div>

                      {/* Hours Row */}
                      <div className="flex items-start gap-3 pt-3 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-[#FAF8F5] flex items-center justify-center shrink-0">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-time-line text-[#1B3D2A] text-sm" />
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-[#9A8A7A] mb-1.5">營業時間</p>
                          <ul className="space-y-1">
                            {store.hours.map((line, li) => (
                              <li key={li} className="text-xs text-[#1B3D2A] font-medium leading-snug flex items-start gap-1.5">
                                <span className="block w-1 h-1 rounded-full bg-[#D4A574] mt-[5px] shrink-0" />
                                <span>{line}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Footer Action */}
                      <div className="mt-4 pt-3 border-t border-[#E8E0D6]/60">
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(store.addr)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#1B3D2A] hover:text-[#D4A574] transition-colors group/link"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-external-link-line text-sm" />
                          </div>
                          <span>在 Google 地圖開啟</span>
                          <div className="w-3.5 h-3.5 flex items-center justify-center opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all duration-200">
                            <i className="ri-arrow-right-line text-[11px]" />
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave: white to warm→CTA */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* 亞洲市場展望 Roadmap */}
        <div className="bg-[#FAF8F5] py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-rocket-line text-[#1B3D2A]" />
                  </div>
                </div>
                <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">Expansion Roadmap</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">亞洲市場擴張藍圖</h3>
              <p className="text-sm text-[#5A6B5A] mt-1">以台灣成功經驗為根基，逐步拓展亞太服務網絡</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {[
                { city: '東京', country: '日本', year: '2027 Q2', icon: 'ri-building-2-line', status: '籌備中' },
                { city: '首爾', country: '韓國', year: '2027 Q4', icon: 'ri-building-2-line', status: '規劃中' },
                { city: '上海', country: '中國', year: '2028 Q1', icon: 'ri-building-2-line', status: '評估中' },
                { city: '新加坡', country: '新加坡', year: '2028 Q3', icon: 'ri-building-2-line', status: '評估中' },
                { city: '曼谷', country: '泰國', year: '2029 Q1', icon: 'ri-building-2-line', status: '長期規劃' },
                { city: '吉隆坡', country: '馬來西亞', year: '2029 Q3', icon: 'ri-building-2-line', status: '長期規劃' },
              ].map((city, idx) => (
                <SectionReveal key={city.city} delay={idx}>
                  <div className="group bg-white rounded-xl border border-[#E8E0D6]/60 p-4 md:p-5 text-center hover:border-[#D4A574]/40 hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#D4A574]/0 via-[#D4A574] to-[#D4A574]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="w-10 h-10 rounded-full bg-[#1B3D2A]/8 flex items-center justify-center mx-auto group-hover:bg-[#1B3D2A]/15 transition-colors">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${city.icon} text-[#1B3D2A] text-lg`} />
                      </div>
                    </div>
                    <h4 className="mt-2 text-sm font-bold text-[#1B3D2A]">{city.city}</h4>
                    <p className="text-[11px] text-[#5A6B5A] mt-0.5">{city.country}</p>
                    <div className="mt-2 flex items-center justify-center gap-1.5">
                      <span className="text-[10px] font-semibold text-[#D4A574] bg-[#D4A574]/10 px-2 py-0.5 rounded-full">{city.year}</span>
                    </div>
                    <span className="inline-block mt-1.5 text-[10px] text-[#9A8A7A] italic">{city.status}</span>
                  </div>
                </SectionReveal>
              ))}
            </div>

            <SectionReveal delay={6} className="mt-8 md:mt-10 text-center">
              <p className="text-sm text-[#5A6B5A] leading-relaxed max-w-2xl mx-auto">
                富聆聽健康秉持「讓每個人都能聽見世界的美好」的使命，
                以台灣為起點，逐步將專業聽力照護服務拓展至全亞洲，
                讓更多聽損者享有高品質的聽力保健服務。
              </p>
            </SectionReveal>
          </div>
        </div>

        {/* ===== CTA ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-customer-service-2-line text-[#1B3D2A]" />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">開始行動</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                  準備好開始您的聽力健康之旅了嗎？
                </h3>
                <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                  立即預約免費聽力檢測，讓我們的專業團隊為您提供個人化的聽力保健建議。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/booking')}
                  className="bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-check-line" />
                  </div>
                  免費預約檢測
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="border border-[#E8E0D6] text-[#5A6B5A] px-6 md:px-8 py-3 rounded-xl text-sm font-semibold hover:border-[#1B3D2A] hover:text-[#1B3D2A] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line" />
                  </div>
                  查看門市資訊
                </button>
              </div>
            </div>
          </SectionReveal>
        </div>
      </main>

      <Footer />
    </div>
  );
}